import Vue from 'vue';
import styled, { css } from 'vue-styled-components';
import { darken } from 'polished';
import { debounce } from 'lodash';

import * as C from '../common/WidgetSettingPanel/options/styles';
import {
    FLEX_CENTER,
    applyHoverAndPress,
    BLUE,
    square,
    GRAY,
    RED,
} from '@/styles/common.styled';

import {
    FLEX_VERTICAL_CENTER_DIV,
    FLEX_COLUMN_DIV,
    GRAY_100_SPAN,
    GRAY_100_DIV,
    FLEX_CENTER_DIV,
    FLEX_COLUMN_ALIGN_CENTER_DIV,
} from '@styles/common.styled.component';
RingLoader;

import PlusIcon from '@svgIcons/plus.svg';
import CloseIcon from '@svgIcons/close.svg';
import CSVIcon from '@svgIcons/csv_icon.svg';
import DocumentIcon from '@svgIcons/document_stroke.svg';

import { isValueExist } from '@/utils/utils';
import { NOTI_TYPES } from '@/store/dashboardEdit';
import RingLoader from '@/components/Common/RingLoader';

const UPLOADABLE_CSV = '.csv';
const UPLOADABLE_XLSX = '.xlsx';
// File connectors that accept something other than CSV, keyed by asset type.
// Naver Blog exports its stats as xlsx; a hand-converted csv breaks on the
// Korean encoding, so the server rejects it on purpose (TASK-14634).
const UPLOADABLE_FILE_TYPES = {
    naverBlogReport: UPLOADABLE_XLSX,
};
// Copy that names the file format, keyed by the accepted extensions
const UPLOAD_COPY_KEYS = {
    [UPLOADABLE_CSV]: {
        guide: 'CONNECTION.upload_second_panel_sub_description',
        defaultError: 'CONNECTION.upload_error_default',
    },
    [UPLOADABLE_XLSX]: {
        guide: 'CONNECTION.upload_second_panel_sub_description_xlsx',
        defaultError: 'CONNECTION.upload_error_default_xlsx',
    },
};
// The server's code for a file whose extension the connector does not accept,
// and what it says today, before it sends that code (TASK-14634)
const FILE_EXTENSION_NOT_ALLOWED = 'FILE_EXTENSION_NOT_ALLOWED';
const FILE_EXTENSION_NOT_ALLOWED_MESSAGE = 'File extension not allowed';

export const getUploadableFileTypes = (assetType) =>
    R.propOr(UPLOADABLE_CSV, assetType, UPLOADABLE_FILE_TYPES);

export const getUploadCopyKeys = (accept) =>
    R.propOr(UPLOAD_COPY_KEYS[UPLOADABLE_CSV], accept, UPLOAD_COPY_KEYS);

// ".csv,.xlsx" -> ".csv, .xlsx", for the copy that lists the accepted formats
const formatExtensions = (extensions) =>
    R.compose(
        R.join(', '),
        R.filter(Boolean),
        R.map(R.trim),
        R.unless(Array.isArray, R.split(','))
    )(extensions);

// The file list shows the CSV badge only for csv files; anything else (the
// xlsx of naverBlogReport) gets the plain document icon.
const isCsvFileName = (fileName) => /\.csv$/i.test(fileName);

// Same rule as Buefy's BUpload.checkType (0.7.4): the extension has to match
// one of the accept entries exactly, case included. A file this rejects is
// dropped by the uploader before `input` fires, so it is the one to report.
const isAcceptedByUploader = (fileName, accept) => {
    const extIndex = fileName.lastIndexOf('.');
    const extension = extIndex >= 0 ? fileName.substring(extIndex) : '';
    return R.compose(
        R.any(R.equals(extension)),
        R.filter(Boolean),
        R.map(R.trim),
        R.split(',')
    )(accept || '');
};

const SAMPLE_ASSET_ID = 10416;
const INVALID_CSV_TRUNCATED_DUPLICATE_COLUMN_NAME =
    'INVALID_CSV_TRUNCATED_DUPLICATE_COLUMN_NAME';
const CSV_COLUMN_NAME_TRUNCATED = 'CSV_COLUMN_NAME_TRUNCATED';

const UPLOAD_WARNING_FORMATTERS = {
    [CSV_COLUMN_NAME_TRUNCATED]: ({ properties = {}, $t }) => {
        const columns = R.pathOr([], ['details', 'columns'], properties);
        const warningDetailLines = columns
            .map(({ originalName = '', normalizedName = '' }) => {
                if (!originalName) {
                    return '';
                }

                return normalizedName
                    ? `${originalName} -> ${normalizedName}`
                    : originalName;
            })
            .filter(isValueExist);

        return {
            toggleMessage: warningDetailLines.join('\n'),
        };
    },
};

const getUploadWarningLocale = ({ data = {}, $t, translateOr }) => {
    const { code = '', message = '' } = data;
    const warningLocaleKey = `CONNECTION.upload_warning_${code}`;
    const formatter = R.prop(code, UPLOAD_WARNING_FORMATTERS);
    const warningTitle = translateOr(
        warningLocaleKey,
        message || $t('CONNECTION.upload_warning_default')
    );

    if (!formatter) {
        return {
            title: warningTitle,
            toggleMessage: '',
        };
    }

    const { toggleMessage = '' } = formatter({
        properties: data.properties,
        $t,
    });

    return {
        title: warningTitle,
        toggleMessage,
    };
};

const UPLOAD_ERROR_FORMATTERS = {
    [INVALID_CSV_TRUNCATED_DUPLICATE_COLUMN_NAME]: ({ properties = {} }) => {
        const duplicatedColumnNames = R.compose(
            R.join(', '),
            R.uniq,
            R.reject(R.isEmpty),
            R.unnest,
            R.map(R.compose(R.defaultTo([]), R.prop('originals'))),
            R.defaultTo([])
        )(properties.duplicatedAfterNormalization);

        return { duplicatedColumnNames };
    },
};

// The server still throws the extension rejection as a plain error
// (INTERNAL_ERROR + message) until it sends FILE_EXTENSION_NOT_ALLOWED
const getUploadErrorCode = ({ code = '', message = '' }) =>
    message === FILE_EXTENSION_NOT_ALLOWED_MESSAGE
        ? FILE_EXTENSION_NOT_ALLOWED
        : code;

const getUploadErrorLocale = ({
    data = {},
    $t,
    translateOr,
    accept = UPLOADABLE_CSV,
}) => {
    const code = getUploadErrorCode(data);
    const errorLocaleKey = `CONNECTION.upload_error_${code}`;
    const formatter = R.prop(code, UPLOAD_ERROR_FORMATTERS);

    if (code === FILE_EXTENSION_NOT_ALLOWED) {
        return $t(errorLocaleKey, {
            extensions: formatExtensions(
                R.pathOr(accept, ['properties', 'allowedFileExtensions'], data)
            ),
        });
    }

    if (formatter) {
        return $t(errorLocaleKey, formatter(data));
    }

    return translateOr(
        errorLocaleKey,
        $t(getUploadCopyKeys(accept).defaultError)
    );
};

const uploadFileAPI = ({ assetId, formData }) => {
    return axios
        .post(`/assets/${assetId}/file`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })

        .then(R.prop('data'));
};

const getFiles = ({ assetId }) => {
    return axios.get(`assets/${assetId}/file`).then(R.prop('data'));
};

const deleteFile = ({ assetId, fileId }) => {
    return axios
        .delete(`assets/${assetId}/file/${fileId}`)
        .then(R.prop('data'));
};

export default {
    name: 'FileUploadConnector',
    data() {
        return {
            loading: false,
            files: [],
        };
    },
    props: {
        assetId: {
            type: Number,
        },
        assetType: {
            type: String,
            default: '',
        },
    },
    computed: {
        accept() {
            return getUploadableFileTypes(this.assetType);
        },
    },
    methods: {
        // BUpload silently drops files outside `accept` on drag and drop; the
        // same drop event reaches this root element, so tell the user here.
        notifyRejectedDrop(event) {
            // BUpload prevents the default only on its inner drop zone
            event.preventDefault();
            const files = Array.from(
                R.pathOr([], ['dataTransfer', 'files'], event)
            );

            files
                .filter((file) => !isAcceptedByUploader(file.name, this.accept))
                .forEach((file) => {
                    this.addNotiMsg({
                        type: NOTI_TYPES.FAIL,
                        title:
                            this.$t(
                                `CONNECTION.upload_error_${FILE_EXTENSION_NOT_ALLOWED}`,
                                { extensions: formatExtensions(this.accept) }
                            ) +
                            '\n' +
                            file.name,
                    });
                });
        },
        handleUpload: debounce(function (file) {
            if (!isValueExist(file)) return;

            const promises = file.map((v) => {
                let formData = new FormData();
                formData.append('file', v);
                return uploadFileAPI({ assetId: this.assetId, formData });
            });

            this.loading = true;
            return Promise.allSettled(promises)
                .then((values) => {
                    const uploadWarnings = R.compose(
                        R.unnest,
                        R.map(R.pathOr([], ['value', 'warnings'])),
                        R.filter(R.propEq('status', 'fulfilled'))
                    )(values);

                    if (isValueExist(uploadWarnings)) {
                        uploadWarnings.forEach((warning = {}) => {
                            const {
                                title,
                                toggleMessage = '',
                            } = getUploadWarningLocale({
                                data: warning,
                                $t: (...args) => this.$t(...args),
                                translateOr: (...args) =>
                                    this.translateOr(...args),
                            });

                            this.addNotiMsg({
                                type: NOTI_TYPES.IN_PROGRESS,
                                title,
                                ...(toggleMessage && {
                                    toggleInfo: {
                                        triggerText: this.$t(
                                            'COMMON.cta_google_SEE_MORE'
                                        ),
                                        message: toggleMessage,
                                    },
                                }),
                            });
                        });
                    }

                    const rejectedList = R.filter(
                        R.propEq('status', 'rejected'),
                        values
                    );

                    if (isValueExist(rejectedList)) {
                        rejectedList.forEach((error) => {
                            const errorData = R.pathOr(
                                {},
                                ['reason', 'response', 'data'],
                                error
                            );
                            const {
                                message = '',
                                properties: { filename } = {},
                            } = errorData;

                            const fileName = filename
                                ? filename
                                : /\.(csv|xlsx)$/.test(message)
                                ? R.replace(
                                      /(invalid csv file: )/g,
                                      '',
                                      message
                                  )
                                : '';

                            const codeLocale = getUploadErrorLocale({
                                data: errorData,
                                $t: (...args) => this.$t(...args),
                                translateOr: (...args) =>
                                    this.translateOr(...args),
                                accept: this.accept,
                            });

                            return this.addNotiMsg({
                                type: NOTI_TYPES.FAIL,
                                ...(codeLocale && {
                                    title:
                                        codeLocale +
                                        (fileName ? '\n' + fileName : ''),
                                }),
                            });
                        });
                    }

                    this.getAssetFiles();
                })
                .finally(() => {
                    this.loading = false;
                });
        }, 200),
        getAssetFiles() {
            this.loading = true;
            return getFiles({ assetId: this.assetId })
                .then((files) => {
                    if (files) {
                        this.files = files;
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        deleteFile(fileId) {
            this.loading = true;
            return deleteFile({ assetId: this.assetId, fileId })
                .then(this.getAssetFiles)
                .finally(() => {
                    this.loading = false;
                });
        },
        renderNoFiles() {
            return (
                <DropArea>
                    <TitleWrapper>
                        <MainUploadTitle>
                            {this.$t('CONNECTION.upload_no_file_main_title')}
                        </MainUploadTitle>
                        <SubUploadTitle>
                            {this.$t('CONNECTION.upload_no_file_sub_title')}
                        </SubUploadTitle>
                        <UploadDescription
                            domPropsInnerHTML={this.$t(
                                'CONNECTION.upload_no_file_description'
                            )}
                        ></UploadDescription>
                    </TitleWrapper>
                </DropArea>
            );
        },
        renderFiles() {
            return (
                <FileWrapper>
                    <FileListWrapper onClick={(e) => e.preventDefault()}>
                        {this.files.map((file) => {
                            return (
                                <FileEachWrapper>
                                    <FileEachName>
                                        {isCsvFileName(file.name) ? (
                                            <CSVIcon v-view-box />
                                        ) : (
                                            // keeps its own 20x20 viewBox
                                            <DocumentIcon />
                                        )}
                                        {file.name}
                                    </FileEachName>
                                    <CloseIcon
                                        v-view-box
                                        fill={BLUE(60)}
                                        onClick={() => this.deleteFile(file.id)}
                                    />
                                </FileEachWrapper>
                            );
                        })}
                    </FileListWrapper>
                </FileWrapper>
            );
        },
        onClickReset() {
            this.loading = true;

            const promises = this.files.map(({ id }) =>
                deleteFile({ assetId: this.assetId, fileId: id })
            );

            return Promise.all(promises)
                .then(this.getAssetFiles)
                .finally(() => (this.loading = false));
        },
        renderLoader() {
            return (
                <LoaderWrapper>
                    <RingLoader />
                </LoaderWrapper>
            );
        },
    },
    mounted() {
        if (this.assetId) {
            this.getAssetFiles();
        }
    },
    render() {
        const hasFiles = isValueExist(this.files);
        return (
            <ListWrapper>
                <C.TitleLabel>
                    <span>{this.$t('CONNECTION.upload_box_main_title')}</span>
                    {hasFiles && (
                        <RestSpan onClick={this.onClickReset}>
                            {this.$t('COMMON.reset')}
                        </RestSpan>
                    )}
                </C.TitleLabel>
                {this.loading && this.renderLoader()}
                <ItemWrapper>
                    <Uploader
                        value={[]}
                        multiple
                        drag-drop
                        accept={this.accept}
                        onInput={this.handleUpload}
                        nativeOnDrop={this.notifyRejectedDrop}
                        hasFiles={hasFiles}
                    >
                        {this.renderNoFiles()}
                    </Uploader>
                </ItemWrapper>
                {hasFiles && this.renderFiles()}
            </ListWrapper>
        );
    },
};

const ListWrapper = styled(C.ListWrapper)`
    background: #fff;
    position: relative;
`;

const Uploader = styled(Vue.options.components.BUpload, { hasFiles: Boolean })`
    .upload-draggable {
        ${FLEX_CENTER};
        height: 182px;
        border-radius: 4px;
        border: none;

        input {
            bottom: 0;
        }

        @media screen and (max-width: 767px) {
            height: 104px;
        }

        ${({ hasFiles }) =>
            hasFiles
                ? css`
                      cursor: default;
                      &.is-primary {
                          &:hover {
                              background: #fff;
                          }
                      }
                  `
                : css`
                      &.is-primary {
                          ${applyHoverAndPress('white', 'background-color')};
                      }

                      &.is-primary.is-hovered {
                          background-color: ${darken(0.05, 'white')};
                      }
                  `}
    }
`;

const DropArea = styled(FLEX_COLUMN_ALIGN_CENTER_DIV)``;

const TitleWrapper = styled.div`
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
`;

const MainUploadTitle = styled(GRAY_100_DIV)`
    font-weight: 600;
    margin-bottom: 8px;
`;

const SubUploadTitle = styled(GRAY_100_DIV)`
    font-size: 14px;
`;

const UploadButton = styled.div`
    width: 100%;
    background: ${BLUE(60)};
    position: absolute;
    bottom: 0;
    left: 0;
    cursor: pointer;
    border-radius: 4px;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    font-size: 14px;
    & > svg {
        margin-right: 4px;
        ${square(16)};
    }
`;

const FileWrapper = styled(FLEX_COLUMN_DIV)`
    width: 100%;
    height: 184px;
    border-top: 1px solid ${GRAY(20)};
`;

const FileListWrapper = styled(FLEX_COLUMN_DIV)`
    width: 100%;
    height: 140px;
    overflow: auto;
    padding: 8px 12px;
`;

const FileEachWrapper = styled(FLEX_VERTICAL_CENTER_DIV)`
    background: ${BLUE(10)};
    border-radius: 14px;
    padding: 8px;
    margin-bottom: 4px;
    justify-content: space-between;
    & > svg {
        ${square(14)};
        cursor: pointer;
    }
`;

const FileEachName = styled(FLEX_VERTICAL_CENTER_DIV)`
    font-size: 12px;
    color: ${BLUE(60)};
    & > svg {
        ${square(16)};
        margin-right: 4px;
    }
`;

const RestSpan = styled(GRAY_100_SPAN)`
    cursor: pointer;
    color: ${BLUE(60)};
`;

const ItemWrapper = styled(C.ItemWrapper)`
    padding: 0;
`;

const LoaderWrapper = styled(FLEX_CENTER_DIV)`
    top: 0;
    left: 0;
    position: absolute;
    background: #fff;
    opacity: 0.8;
    width: 100%;
    height: 410px;
    z-index: 4;
`;

const UploadDescription = styled(SubUploadTitle)`
    color: ${RED(60)};
    white-space: pre-line;
    margin-top: 8px;
    text-align: center;
`;
